import { createContext, useContext, useEffect } from 'react'
import type { RootStore } from './RootStore'
import { bg } from 'lib/background'

export const StoreContext = createContext<RootStore | null>(null)

export function useRootStore(): RootStore {
  const store = useContext(StoreContext)
  if (!store) throw new Error('useRootStore must be used within StoreContext.Provider')
  return store
}

/**
 * Minimal shape every EntityStore exposes for a list read. Kept structural (not
 * `EntityStore<...>`) so the element type `T` infers from the concrete store
 * passed in — callers never write a type argument.
 */
type ListReadStore = {
  readonly hits: readonly unknown[]
  readonly listLoading: boolean
  readonly listError: string | undefined
  fetchList: (...args: never[]) => Promise<unknown>
}

/**
 * Fetch a store's list once on mount and return its reactive view. Moves the
 * `useEffect(() => store.fetchList())` boilerplate and the component-local
 * `isLoading`/list `useState` into one hook: the loading and error flags come
 * straight from the store's observables (the single source of truth), so a
 * component that reads them must be an `observer`.
 *
 * Pass `params` to filter the list (it refetches when they change) and `skip` to hold the
 * fetch until its inputs exist.
 *
 * `T` infers from the store (e.g. `useEntityList(useLocationStore())` →
 * `LocationType`), so no explicit type argument is needed.
 */
export function useEntityList<S extends ListReadStore>(
  store: S,
  { params, skip = false }: { params?: Parameters<S['fetchList']>[0]; skip?: boolean } = {},
): { list: S['hits'][number][]; loading: boolean; error: string | undefined } {
  const paramsKey = JSON.stringify(params ?? null)
  useEffect(() => {
    if (skip) return
    bg(() => store.fetchList(params as never))
    // Fetch on mount and whenever the params change; the store caches and dedupes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, skip])
  return {
    list: skip ? [] : (store.hits as S['hits'][number][]),
    loading: !skip && store.listLoading,
    error: skip ? undefined : store.listError,
  }
}

export function useAuthStore() {
  return useRootStore().auth
}
export function useContactStore() {
  return useRootStore().contacts
}
export function useActivityStore() {
  return useRootStore().activities
}
export function useTaskStore() {
  return useRootStore().tasks
}
export function useAppointmentStore() {
  return useRootStore().appointments
}
export function usePatientStore() {
  return useRootStore().patients
}
export function useTransactionStore() {
  return useRootStore().transactions
}
export function useMessageStore() {
  return useRootStore().messages
}
export function useCallStore() {
  return useRootStore().calls
}
export function useUserStore() {
  return useRootStore().users
}
export function useRoleStore() {
  return useRootStore().roles
}
export function useWorkspaceStore() {
  return useRootStore().workspaces
}
export function useLocationStore() {
  return useRootStore().locations
}
/**
 * Data hook: loads the workspace locations on mount and returns the reactive
 * list + loading/error from LocationStore. Read in an `observer` component.
 */
export function useLocations() {
  return useEntityList(useLocationStore())
}
export function useFollowUpStore() {
  return useRootStore().followUps
}
export function useTagColorStore() {
  return useRootStore().tagColors
}
export function usePipelineStore() {
  return useRootStore().pipelines
}
export function useListStore() {
  return useRootStore().lists
}
export function useListMembershipStore() {
  return useRootStore().listMemberships
}
export function useSystemListStore() {
  return useRootStore().systemLists
}
export function useNotificationStore() {
  return useRootStore().notifications
}
export function useTreatmentPlanStore() {
  return useRootStore().treatmentPlans
}
export function useProcedureStore() {
  return useRootStore().procedures
}
export function useAdCampaignStore() {
  return useRootStore().adCampaigns
}
export function useAdPlatformMappingStore() {
  return useRootStore().adPlatformMappings
}
export function useAppointmentTypeStore() {
  return useRootStore().appointmentTypes
}
export function useBudgetStore() {
  return useRootStore().budgets
}
export function useCdtCodeMappingStore() {
  return useRootStore().cdtCodeMappings
}
export function useChannelMappingStore() {
  return useRootStore().channelMappings
}
export function useConversionLogStore() {
  return useRootStore().conversionLogs
}
export function useSmsTemplateStore() {
  return useRootStore().smsTemplates
}
export function useTaskTemplateStore() {
  return useRootStore().taskTemplates
}
export function useNoteTemplateStore() {
  return useRootStore().noteTemplates
}
export function useExtractionPromptStore() {
  return useRootStore().extractionPrompts
}
export function useFeatureToggleStore() {
  return useRootStore().featureToggles
}
export function useFeatureToggleDefinitionStore() {
  return useRootStore().featureToggleDefinitions
}
export function useFormFieldMappingStore() {
  return useRootStore().formFieldMappings
}
export function useUnmappedFormKeyStore() {
  return useRootStore().unmappedFormKeys
}
export function useImportStore() {
  return useRootStore().imports
}
export function useIncomingCallStore() {
  return useRootStore().incomingCalls
}
export function useProviderStore() {
  return useRootStore().providers
}
export function useScorecardStore() {
  return useRootStore().scorecards
}
export function useUserSettingStore() {
  return useRootStore().userSettings
}
export function useUtmAliasStore() {
  return useRootStore().utmAliases
}
export function useCustomPageStore() {
  return useRootStore().customPages
}
export function useWorkspaceInviteStore() {
  return useRootStore().workspaceInvites
}

export function useVoiceAgentOpsStore() {
  return useRootStore().voiceAgentOps
}

export function useSmsAgentOpsStore() {
  return useRootStore().smsAgentOps
}

export function useAIAgentStore() {
  return useRootStore().aiAgent
}
